// DocumentPreviewModal — inline preview of a SharePoint-backed
// document via Microsoft's preview endpoint. Microsoft renders the
// file (PDFs, Word, Excel, PowerPoint, images) in an iframe served
// from Office Online.
//
// v1.x phase 11.16 (Tier B item 6 completion). The "Open in
// SharePoint" deep-link still exists for power users who want the
// full SharePoint UI; this modal is the no-context-switch reading
// path.
//
// The preview URL is short-lived (~5 min) so we re-fetch every time
// the user opens the modal — no caching.

import { ExternalLink } from "lucide-react";
import { useEffect, useState } from "react";

import { ApiError, apiV11, resolveMediaUrl } from "../api";
import { Button, ErrorBanner } from "../ui";
import { Modal } from "./Modal";

export interface DocumentPreviewModalProps {
  open: boolean;
  documentId: string | null;
  filename: string;
  // Optional advisor-side deep-link to SharePoint web UI. Surfaces
  // an "Open in SharePoint" link inside the modal so the user can
  // pop out for editing.
  sharepointWebUrl?: string | null;
  onClose: () => void;
  // Called when the server says the document is archived, so the list
  // behind the modal can reload and drop the stale row.
  onArchived?: () => void;
}

interface PreviewState {
  loading: boolean;
  error: string | null;
  url: string | null;
}

export function DocumentPreviewModal({
  open,
  documentId,
  filename,
  sharepointWebUrl,
  onClose,
  onArchived,
}: DocumentPreviewModalProps) {
  const [state, setState] = useState<PreviewState>({
    loading: true,
    error: null,
    url: null,
  });

  useEffect(() => {
    if (!open || !documentId) {
      setState({ loading: true, error: null, url: null });
      return;
    }
    let cancelled = false;
    setState({ loading: true, error: null, url: null });
    void (async () => {
      try {
        const resp = await apiV11.getDocumentPreviewUrl(documentId);
        if (cancelled) return;
        setState({ loading: false, error: null, url: resp.preview_url });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 410) onArchived?.();
        const msg =
          err instanceof ApiError
            ? err.message
            : "Couldn't load preview.";
        setState({ loading: false, error: msg, url: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, documentId]);

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Preview · ${filename}`}
      footer={
        <>
          {sharepointWebUrl && documentId ? (
            // Same brokered path as the Documents tab row: the OS
            // grants access to this one file and redirects. Linking
            // at the raw sharepoint_web_url only worked for the two
            // SharePoint site owners (Nate, 2026-08-05).
            <a
              href={resolveMediaUrl(`/api/files/open/${documentId}`) ?? "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="document-preview-modal-open-sharepoint"
              style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
            >
              Open in SharePoint
              <ExternalLink size={14} strokeWidth={1.5} aria-hidden />
            </a>
          ) : null}
          <Button variant="primary" onClick={onClose} type="button">
            Close
          </Button>
        </>
      }
      className="document-preview-modal"
    >
      <div className="document-preview-modal-body">
        {state.loading ? (
          <div className="document-preview-modal-loading">
            Loading preview…
          </div>
        ) : state.error ? (
          <ErrorBanner message={state.error} />
        ) : state.url ? (
          <iframe
            className="document-preview-modal-iframe"
            src={state.url}
            title={`Preview of ${filename}`}
            // Microsoft's preview frame uses cross-origin POSTs to
            // the Office Online host; allow-same-origin would break
            // it. Default sandbox flags suffice.
          />
        ) : null}
      </div>
    </Modal>
  );
}
